import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import evaluationsApi from '../../api/evaluations';

// Async thunks
export const fetchEvaluationSummary = createAsyncThunk(
  'evaluations/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const data = await evaluationsApi.getSummary();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchDirtyItems = createAsyncThunk(
  'evaluations/fetchDirtyItems',
  async (type = 'both', { rejectWithValue }) => {
    try {
      const data = await evaluationsApi.getDirtyItems(type);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const createEvaluation = createAsyncThunk(
  'evaluations/create',
  async (options, { rejectWithValue }) => {
    try {
      const data = await evaluationsApi.createEvaluation(options);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchEvaluation = createAsyncThunk(
  'evaluations/fetchOne',
  async (requestId, { rejectWithValue }) => {
    try {
      const data = await evaluationsApi.getEvaluation(requestId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchEvaluationHistory = createAsyncThunk(
  'evaluations/fetchHistory',
  async (options = {}, { rejectWithValue }) => {
    try {
      const data = await evaluationsApi.getEvaluations(options);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

const initialState = {
  // Summary data
  summary: null,
  summaryLoading: false,
  summaryError: null,

  // Dirty items
  dirtyItems: {
    events: [],
    strategies: []
  },
  dirtyItemsLoading: false,
  dirtyItemsError: null,

  // Current evaluation request
  currentEvaluation: null,
  currentEvaluationLoading: false,
  currentEvaluationError: null,

  // Evaluation history
  history: [],
  historyLoading: false,
  historyError: null,
  historyLastKey: null,

  // UI state
  isModalOpen: false,
  isPolling: false,
  pollingRequestId: null,

  // Toast/notification
  successMessage: null,
  errorMessage: null
};

const evaluationsSlice = createSlice({
  name: 'evaluations',
  initialState,
  reducers: {
    openEvaluationModal: (state) => {
      state.isModalOpen = true;
    },
    closeEvaluationModal: (state) => {
      state.isModalOpen = false;
    },
    startPolling: (state, action) => {
      state.isPolling = true;
      state.pollingRequestId = action.payload;
    },
    stopPolling: (state) => {
      state.isPolling = false;
      state.pollingRequestId = null;
    },
    updatePolledEvaluation: (state, action) => {
      state.currentEvaluation = action.payload;
    },
    clearMessages: (state) => {
      state.successMessage = null;
      state.errorMessage = null;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setCurrentEvaluationError: (state, action) => {
      state.currentEvaluationError = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
    resetEvaluationState: (state) => {
      state.currentEvaluation = null;
      state.currentEvaluationError = null;
      state.isPolling = false;
      state.pollingRequestId = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch summary
    builder
      .addCase(fetchEvaluationSummary.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError = null;
      })
      .addCase(fetchEvaluationSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchEvaluationSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError = action.payload?.message || 'Failed to fetch summary';
      });

    // Fetch dirty items
    builder
      .addCase(fetchDirtyItems.pending, (state) => {
        state.dirtyItemsLoading = true;
        state.dirtyItemsError = null;
      })
      .addCase(fetchDirtyItems.fulfilled, (state, action) => {
        state.dirtyItemsLoading = false;
        state.dirtyItems = action.payload;
      })
      .addCase(fetchDirtyItems.rejected, (state, action) => {
        state.dirtyItemsLoading = false;
        state.dirtyItemsError = action.payload?.message || 'Failed to fetch dirty items';
      });

    // Create evaluation
    builder
      .addCase(createEvaluation.pending, (state) => {
        state.currentEvaluationLoading = true;
        state.currentEvaluationError = null;
        state.successMessage = null;
      })
      .addCase(createEvaluation.fulfilled, (state, action) => {
        state.currentEvaluationLoading = false;
        state.currentEvaluation = action.payload;
        state.isModalOpen = true;
        state.isPolling = true;
        state.pollingRequestId = action.payload.requestId;
      })
      .addCase(createEvaluation.rejected, (state, action) => {
        state.currentEvaluationLoading = false;
        state.currentEvaluationError = action.payload?.message || 'Failed to create evaluation';
        state.errorMessage = action.payload?.message || 'Failed to create evaluation';
      });

    // Fetch single evaluation
    builder
      .addCase(fetchEvaluation.pending, (state) => {
        state.currentEvaluationLoading = true;
      })
      .addCase(fetchEvaluation.fulfilled, (state, action) => {
        state.currentEvaluationLoading = false;
        state.currentEvaluation = action.payload;
      })
      .addCase(fetchEvaluation.rejected, (state, action) => {
        state.currentEvaluationLoading = false;
        state.currentEvaluationError = action.payload?.message || 'Failed to fetch evaluation';
      });

    // Fetch history
    builder
      .addCase(fetchEvaluationHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchEvaluationHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload.items || [];
        state.historyLastKey = action.payload.lastEvaluatedKey || null;
      })
      .addCase(fetchEvaluationHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload?.message || 'Failed to fetch history';
      });
  }
});

export const {
  openEvaluationModal,
  closeEvaluationModal,
  startPolling,
  stopPolling,
  updatePolledEvaluation,
  clearMessages,
  setSuccessMessage,
  setCurrentEvaluationError,
  setErrorMessage,
  resetEvaluationState
} = evaluationsSlice.actions;

export default evaluationsSlice.reducer;
